<template>
  <div
    v-if="doc"
    class="header header--book header--book-preview"
    :class="{ 'header--book-preview-rounded': isRounded }"
  >
    <BackLink class="header__backlink" />
    <div class="header__controls icon-toolbar">
      <DocListsMenu
        class="book-preview-favorite-btn icon-toolbar__item icon-toolbar__item--separator"
        :doc="doc"
        positon="center"
      />
      <ExportBookButton
        v-if="doc.showDownload"
        class="book-preview-download-btn icon-toolbar__item icon-toolbar__item--separator"
        :doc="doc"
      />
      <DocLibraryMenu
        class="book-preview-actions-btn icon-toolbar__item icon-toolbar__item--separator"
        :doc="doc"
        position="right"
      ></DocLibraryMenu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onBeforeUnmount, ref } from 'vue'

import BackLink from '@/app/components/BackLink.vue'
import DocLibraryMenu from '@/app/components/doc/DocLibraryMenu.vue'
import DocListsMenu from '@/app/components/doc/DocListsMenu.vue'
import ExportBookButton from '@/app/components/doc/preview/ExportBookButton.vue'
import { emitter } from '@/services/mitt'
import { DocView } from '@/models/doc'

defineProps<{
  doc?: DocView
}>()

const isRounded = ref(false)

onBeforeMount(() => {
  emitter.on('appScroll', scrollHandler)
})

onBeforeUnmount(() => {
  emitter.off('appScroll')
})

function scrollHandler(): void {
  // Get current scrolling position
  const scrollTop = (document.getElementById('app') as HTMLElement).scrollTop

  // Get height of the book block - it's different on different widths
  const bookBlockHeight = (
    document.querySelector('.doc-preview__cover') as HTMLElement
  ).offsetHeight

  // Book block has roundings, and we need to apply rounding to the header
  // before it reaches the roundings of the book block.
  const maxScroll = bookBlockHeight - 8
  isRounded.value = scrollTop > maxScroll
}
</script>

<style lang="scss" scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 13px;
  background: $c-black-5;
  @media only screen and (max-width: $w-medium-s) {
    @supports (padding-top: env(safe-area-inset-top)) {
      padding: calc(env(safe-area-inset-top) + 13px) 13px 13px 13px;
    }
  }
  @media only screen and (min-width: $w-tablet-s) {
    display: none;
  }

  &--book-preview-rounded,
  &--book-preview-rounded:before,
  &--book-preview-rounded:after {
    border-radius: 0 0 14px 14px;
  }

  &__backlink {
    margin-top: 3px;
  }
}

:deep(.action-menu__button) {
  color: $c-black-6;
  @include hover {
    color: $c-black-2;
    background: transparent;
  }
}

:deep(.action-menu__list) {
  --horizontal-distance: 8px;
}

.night-mode .header {
  background: $c-dark-2;
}
</style>

<style lang="scss">
.night-mode .icon-toolbar__item .action-menu__button {
  color: $c-black-11;
  @include hover {
    color: $c-black-11;
    background: transparent;
  }
}
</style>
